import {
    formatIncomeSplitPercentages,
    type IncomeSplitConfigLegacy,
} from '@fruitful/income-split';
import { useAtom } from 'jotai';
import { useEffect } from 'react';

import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { incomeSplitAtomLegacy } from '../../state/PaycheckRoutingAtoms';
import { PaycheckRoutingStatus } from '../../types/PaycheckRouting';
import { isAccountValidAsIncomeSplitSource } from '../../utils/IncomeSplitUtils';
import useGetExistingPaycheckConfigs from '../paycheck-routing/useGetPaycheckConfigs';
import useGetAllBankAccountDetailsFromGlobalState from '../useGetAllBankAccountDetailsFromGlobalState';

const useGetIncomeSplitFromGlobalState = () => {
    const user = useAuthedUser();

    const [incomeSplit, setIncomeSplit] = useAtom(incomeSplitAtomLegacy);

    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetailsFromGlobalState();

    const {
        paycheckRoutingConfigs,
        isLoading: isLoadingPaycheckRoutingConfigs,
    } = useGetExistingPaycheckConfigs();

    const isLoading =
        isLoadingPaycheckRoutingConfigs || isLoadingBankAccountDetails;

    useEffect(() => {
        if (!isLoading) {
            if (
                !incomeSplit &&
                paycheckRoutingConfigs?.[paycheckRoutingConfigs.length - 1]
            ) {
                // we've loaded income split for the first time and there's an existing config
                const config =
                    paycheckRoutingConfigs?.[paycheckRoutingConfigs.length - 1];

                // if this is a legacy paycheck split config, ignore
                if (config?.originator != null && config?.receiver != null) {
                    return;
                }

                const incomeSplit = paycheckRoutingConfigs?.[
                    paycheckRoutingConfigs.length - 1
                ] as unknown as IncomeSplitConfigLegacy;

                const formattedIncomeSplit =
                    formatIncomeSplitPercentages(incomeSplit);

                setIncomeSplit(formattedIncomeSplit);
            } else if (!incomeSplit && !(paycheckRoutingConfigs ?? []).length) {
                // we've determined there's no existing config, create a new one
                setIncomeSplit({
                    membershipId: user.membership?.id ?? '',
                    isActive: true,
                    status: PaycheckRoutingStatus.ACTIVE,
                    accounts: bankAccountDetails
                        .filter((account) => {
                            return isAccountValidAsIncomeSplitSource(
                                user,
                                account,
                            );
                        })
                        .map((account) => {
                            return {
                                accountId: account.bankAccountId,
                            };
                        }),
                    rules: [],
                    onlyExecuteOnPaychecks: false,
                    isNew: true,
                });
            }
        }
    }, [JSON.stringify(paycheckRoutingConfigs), isLoading]);

    return { incomeSplit, isLoading };
};

export default useGetIncomeSplitFromGlobalState;
